import { A, useLocation, useNavigate } from "@solidjs/router";
import { createListRecentChatsRequest, useCallOptions, useChatClient, withBearerToken } from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { StButton } from "@storytell/ui/buttons";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import clsx from "clsx";
import {
  TbBook,
  TbBoxMultiple,
  TbChevronRight,
  TbHelp,
  TbLoader2,
  TbMessage2,
  TbSearch,
  TbSettings,
} from "solid-icons/tb";
import {
  type Component,
  For,
  Match,
  Show,
  Suspense,
  Switch,
  createEffect,
  createResource,
  createSignal,
  onCleanup,
  onMount,
  startTransition,
} from "solid-js";
import { isServer } from "solid-js/web";
import { Avatar } from "~/components/avatar/Avatar";
import { UserAccountPopover } from "~/components/common/UserAccountPopover";
import { ProjectSwitcherModal } from "~/components/projects/ProjectSwitcherModal";
import { useGateActionByAuthenticated } from "~/hooks/useGateActionByAuthenticated";
import { urls } from "~/lib/urls";
import { useWire } from "~/wire";
import { ProjectSwitcher } from "../../projects/ProjectSwitcher";
import { ExtendedPaneMode, NavigationPaneMode, isExtendedPaneVisible, useAppUI } from "./AppUIContext";

// Constants
const MIN_NAV_WIDTH = 250;

/**
 * NavigationPane Component
 *
 * State Machine Logic:
 * - The panel has TWO modes: COLLAPSED (default) or EXPANDED (user clicked to expand)
 * - When COLLAPSED: 48px wide strip with toggle + logo is always visible
 * - When EXPANDED: Full width panel with all navigation content is visible
 * - No hover behavior - purely click-based interaction
 *
 * Visual Behavior:
 * - COLLAPSED: Thin vertical strip flush with left edge, shows toggle icon and logo
 * - EXPANDED: Full navigation panel, integrated sidebar appearance
 * - Content always pushed by sidebar width (48px collapsed, full width expanded)
 * - No floating appearance, no shadows - simple, clean design like Claude/Linear
 */
export const NavigationPane: Component = () => {
  const wire = useWire();
  const { state, actions } = useAppUI();
  const [isResizing, setIsResizing] = createSignal(false);
  const [isMobile, setIsMobile] = createSignal(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnHomepage = () => location.pathname === "/";

  const activeProject = () => {
    const userContext = wire.userContext.get();
    if (!userContext) return undefined;
    return wire.projects.get(userContext.projectId);
  };

  const [isProjectModalOpen, setIsProjectModalOpen] = createSignal(false);
  const openProjectModal = useGateActionByAuthenticated(
    () => ({
      title: "Sign up to switch projects",
      description: "Projects are a great way to collaborate with your team.",
    }),
    () => setIsProjectModalOpen(true),
  );

  let paneRef: HTMLDivElement | undefined;
  let resizeHandleRef: HTMLDivElement | undefined;

  const [recentChats, { refetch, mutate }] = createResource(
    () => wire.auth.isReady() && wire.auth.userId(),
    async () => {
      const client = useChatClient();
      const chats = await client.listRecentChats(
        createListRecentChatsRequest({}),
        useCallOptions(withBearerToken(wire.auth?.token())),
      );

      // TODO: @andi streaming - handle error gracefully
      if (chats.isErr()) {
        return [];
      }

      return chats.value.chats;
    },
  );

  const [chatSearchQuery, setChatSearchQuery] = createSignal("");

  const filteredRecentChats = () => {
    const chats = recentChats();
    if (!chats) return [];
    const query = chatSearchQuery().toLowerCase().trim();
    if (!query) return chats;
    return chats.filter((chat) => chat.title?.toLowerCase().includes(query) ?? false);
  };

  onMount(() => {
    const transitionRefetch = () => {
      startTransition(() => {
        refetch();
      });
    };
    const unsubscribeNewChatCreated = subscribeToGlobalEvent("new-chat-created", transitionRefetch);
    const unsubscribeUserContextChanged = subscribeToGlobalEvent("user-context-changed", transitionRefetch);
    const unsubscribeChatTitleGenerated = subscribeToGlobalEvent("chat-title-generated", (event) => {
      transitionRefetch();
    });

    onCleanup(() => {
      unsubscribeNewChatCreated();
      unsubscribeUserContextChanged();
      unsubscribeChatTitleGenerated();
    });
  });

  // Detect mobile device and screen size
  createEffect(() => {
    if (isServer) return;

    const checkMobile = () => {
      const isMobileScreen = window.innerWidth < 768; // Tailwind md breakpoint
      const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileScreen || isTouchDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    onCleanup(() => {
      window.removeEventListener("resize", checkMobile);
    });
  });

  const onNewChat = () => {
    // Check if already on homepage to provide focus feedback
    const isOnHomepage = !isServer && window.location.pathname === "/";

    // Reset all chat and UI state to initial state
    wire.chat.resetChat();
    actions.resetChat();
    actions.clearExtendedPaneArtifact();
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
    navigate("/");

    // Auto-focus the prompt input when already on homepage
    if (isOnHomepage) {
      // Use setTimeout to ensure the input is ready after state resets
      setTimeout(() => {
        dispatchGlobalEvent("focus-prompt-bar", undefined);
      }, 100);
    }
  };

  // Resize functionality
  const handleResizeStart = (e: MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = state.navigationPane.width;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(MIN_NAV_WIDTH, startWidth + deltaX); // Enforce minimum width

      // Apply width change immediately without batching for responsive feel
      requestAnimationFrame(() => {
        actions.setNavigationPaneWidth(newWidth);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove, { capture: true });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";
    };

    document.addEventListener("mousemove", handleMouseMove, { capture: true });
    document.addEventListener("mouseup", handleMouseUp, { capture: true });
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.body.style.pointerEvents = "none"; // Prevent interference from other elements
  };

  // Toggle handler for manual expand/collapse
  const handleToggle = () => {
    actions.toggleNavigationPane();
  };

  // State machine helpers
  const isExpanded = () => state.navigationPane.mode === NavigationPaneMode.EXPANDED;
  const isCollapsed = () => state.navigationPane.mode === NavigationPaneMode.COLLAPSED;
  const paneWidth = () => state.navigationPane.width;

  const [assetToHighlight, setAssetToHighlight] = createSignal("");
  const [labelToFilterBy, setLabelToFilterBy] = createSignal("");
  onMount(() => {
    const unsubscribeAsset = subscribeToGlobalEvent("open-asset-in-pane", (e) => {
      setAssetToHighlight(e.detail.assetId);
      if (state.navigationPane.secondaryPane !== "assets") {
        actions.setNavigationPaneMode(NavigationPaneMode.EXPANDED);
        actions.openSecondaryPane("assets");
      } else {
        actions.closeSecondaryPane();
      }
    });
    const unsubscribeLabel = subscribeToGlobalEvent("open-label-in-assets-pane", (e) => {
      setLabelToFilterBy(e.detail.labelId);
      if (state.navigationPane.secondaryPane !== "assets") {
        actions.setNavigationPaneMode(NavigationPaneMode.EXPANDED);
        actions.openSecondaryPane("assets");
      }
    });

    onCleanup(() => {
      unsubscribeAsset();
      unsubscribeLabel();
    });
  });
  return (
    <>
      {/* Collapsed strip - always visible, 48px wide */}
      <Show when={isCollapsed()}>
        <aside
          class="fixed left-0 top-0 h-full w-12 bg-canvas border-r border-primary-border z-50 flex flex-col items-center py-4 group"
          aria-label="Collapsed navigation"
        >
          {/* Storytell Logo - clickable to go home (unless on homepage) */}
          <Show
            when={!isOnHomepage()}
            fallback={
              <div class="w-8 h-8">
                <img src="/images/storytell-logo-icon-transparent.svg" alt="Storytell" class="w-8 h-8" />
              </div>
            }
          >
            <StTooltip placement="right" content={<div>Home</div>}>
              <StTooltipTrigger
                as="button"
                type="button"
                onClick={onNewChat}
                class="cursor-pointer transition-all duration-150 ease-out hover:scale-105 active:scale-95"
                aria-label="Go to homepage"
              >
                <img src="/images/storytell-logo-icon.svg" alt="Storytell" class="w-8 h-8" />
              </StTooltipTrigger>
            </StTooltip>
          </Show>

          {/* Expand Button - visible on hover */}
          <StTooltip placement="right" content={<div>Expand navigation</div>}>
            <StTooltipTrigger
              as="button"
              type="button"
              class="p-2 mt-2 hover:bg-canvas-hover transition-all duration-150 focus:outline-none rounded-md opacity-0 group-hover:opacity-100 cursor-pointer"
              onClick={handleToggle}
              aria-label="Expand navigation"
            >
              <TbChevronRight class="w-5 h-5 text-primary-text" />
            </StTooltipTrigger>
          </StTooltip>

          {/* Project Switcher Button */}
          <StTooltip
            placement="right"
            content={
              <div>
                <Show when={activeProject()} fallback="Projects">
                  Project <span class="font-bold">{activeProject()?.name}</span>
                </Show>
              </div>
            }
          >
            <StTooltipTrigger
              as="button"
              type="button"
              class="p-2 mt-2 hover:bg-canvas-hover transition-all duration-150 ease-out hover:scale-105 active:scale-95 focus:outline-none rounded-md cursor-pointer"
              onClick={openProjectModal}
              aria-label="Project"
            >
              <TbBoxMultiple class="w-5 h-5 text-primary-text" />
            </StTooltipTrigger>
          </StTooltip>

          {/* New Chat Button */}
          <StTooltip placement="right" content={<div>New chat</div>}>
            <StTooltipTrigger
              as="button"
              type="button"
              class="p-2 mt-2 bg-brand text-on-brand hover:bg-brand-hover transition-all duration-150 ease-out hover:scale-105 active:scale-95 focus:outline-none rounded-md cursor-pointer"
              onClick={onNewChat}
              aria-label="New chat"
            >
              <svg class="w-5 h-5 text-on-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </StTooltipTrigger>
          </StTooltip>

          {/* All Knowledge Button - only show when user has knowledge */}
          <Show when={wire.assets.searchIds("").length > 0}>
            <StTooltip
              placement="right"
              content={
                <div>
                  All <span class="font-bold">{activeProject()?.name || ""}</span> Knowledge
                </div>
              }
            >
              <StTooltipTrigger
                as="button"
                type="button"
                class="p-2 mt-2 hover:bg-canvas-hover transition-all duration-150 ease-out hover:scale-105 active:scale-95 focus:outline-none rounded-md cursor-pointer"
                onClick={() => wire.knowledgeUi.open()}
                aria-label="All Knowledge"
              >
                <TbBook class="w-5 h-5 text-primary-text" />
              </StTooltipTrigger>
            </StTooltip>
          </Show>

          {/* Recent Chats Button */}
          <StTooltip placement="right" content={<div>Recent chats</div>}>
            <StTooltipTrigger
              as="button"
              type="button"
              class="p-2 mt-2 hover:bg-canvas-hover transition-all duration-150 ease-out hover:scale-105 active:scale-95 focus:outline-none rounded-md cursor-pointer"
              onClick={handleToggle}
              aria-label="Recent chats"
            >
              <TbMessage2 class="w-5 h-5 text-primary-text" />
            </StTooltipTrigger>
          </StTooltip>

          {/* Large spacer to push user avatar to bottom */}
          <div class="flex-1" />

          {/* Help Button */}
          <StTooltip placement="right" content={<div>Help</div>}>
            <StTooltipTrigger
              as="a"
              href="https://help.storytell.ai/"
              target="_blank"
              rel="noopener noreferrer"
              class="p-2 mb-2 hover:bg-canvas-hover transition-all duration-150 ease-out hover:scale-105 active:scale-95 focus:outline-none rounded-md cursor-pointer"
              aria-label="Help"
            >
              <TbHelp class="w-5 h-5 text-primary-text" />
            </StTooltipTrigger>
          </StTooltip>

          {/* User Avatar */}
          <Switch>
            <Match when={!wire.auth?.isReady()}>
              <div class="p-2">
                <TbLoader2 class="animate-spin size-6 text-muted-text" />
              </div>
            </Match>
            <Match when={!wire.auth?.isRegistered()}>
              <A href="/auth/login" class="p-2 hover:bg-canvas-hover transition-colors rounded-md" aria-label="Log in">
                <svg class="w-6 h-6 text-muted-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </A>
            </Match>
            <Match when={true}>
              <StDropdown
                modal={false}
                placement="right-start"
                gutter={8}
                contentProps={{
                  class: "z-50",
                }}
                content={<UserAccountPopover />}
              >
                <StDropdownTrigger
                  as="button"
                  type="button"
                  class="p-1 hover:bg-canvas-hover transition-colors rounded-md cursor-pointer"
                  aria-label="User menu"
                >
                  <Avatar src={wire.auth?.user()?.avatarUrl} name={wire.auth?.user()?.displayName} size="sm" />
                </StDropdownTrigger>
              </StDropdown>
            </Match>
          </Switch>
        </aside>
      </Show>

      {/* Navigation pane - only visible when expanded */}
      <Show when={isExpanded()}>
        <nav
          ref={paneRef}
          class="fixed left-0 top-0 h-full z-50 flex flex-col bg-canvas border-r border-primary-border group transition-[width] duration-300"
          style={{ width: `${paneWidth()}px` }}
          aria-label="Navigation pane"
        >
          {/* Header - fixed at top */}
          <header class="flex-shrink-0 px-4 py-4">
            <div class="flex items-start justify-between gap-3 mb-3">
              {/* Left side: Logo */}
              <Show
                when={!isOnHomepage()}
                fallback={
                  <div class="flex items-center gap-2">
                    <img src="/images/storytell-logo-icon-transparent.svg" alt="Storytell" class="w-8 h-8" />
                  </div>
                }
              >
                <StTooltip placement="top" content={<div>Home</div>}>
                  <StTooltipTrigger
                    as="button"
                    type="button"
                    onClick={onNewChat}
                    class="cursor-pointer flex items-center gap-2"
                    aria-label="Go to homepage"
                  >
                    <img src="/images/storytell-logo-icon.svg" alt="Storytell" class="w-8 h-8" />
                  </StTooltipTrigger>
                </StTooltip>
              </Show>

              {/* Right side: Collapse Button - visible on hover */}
              <StTooltip placement="top" content={<div>Collapse navigation</div>}>
                <StTooltipTrigger
                  as="button"
                  type="button"
                  class="p-2 rounded-md hover:bg-canvas-hover transition-all duration-150 focus:outline-none opacity-0 group-hover:opacity-100 cursor-pointer"
                  onClick={handleToggle}
                  aria-label="Collapse navigation"
                >
                  <TbChevronRight class="w-5 h-5 text-primary-text rotate-180" />
                </StTooltipTrigger>
              </StTooltip>
            </div>

            {/* Project Selector Dropdown */}
            <ProjectSwitcher />
          </header>

          {/* Scrollable content area */}
          <div class="flex-1 overflow-y-auto flex flex-col">
            {/* Action Bar */}
            <div class="flex-shrink-0 px-4 py-3">
              <button
                type="button"
                onClick={onNewChat}
                class="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand text-on-brand hover:bg-brand-hover border border-primary-border rounded-lg transition-all duration-150 ease-out hover:scale-105 active:scale-95 text-sm font-medium cursor-pointer"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                New chat
                <span class="ml-auto text-xs text-on-brand/60 font-mono">⌘ K</span>
              </button>
            </div>

            {/* Chat List */}
            <Show when={wire.assets.searchIds("").length > 0}>
              <div class="px-4 py-3 space-y-2">
                <StButton
                  onClick={() => {
                    wire.knowledgeUi.open();
                  }}
                  icon={TbBook}
                  class="w-full text-base justify-start border border-transparent px-4 data-[active=true]:bg-asset-bg data-[active=true]:text-asset-text data-[active=true]:border-asset-border focus:ring-asset-border"
                  iconClass="size-4"
                  noBorder
                >
                  <span class="flex-auto text-left">All Knowledge</span>
                  <TbChevronRight class="size-4" />
                </StButton>
              </div>
            </Show>

            <Suspense>
              <div class="flex flex-col flex-auto overflow-y-auto px-4 py-2 space-y-2">
                <span class="text-xs text-secondary-text">Recent Chats:</span>

                {/* Chat Search Bar */}
                <div class="relative mb-2">
                  <TbSearch class="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-muted-text" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={chatSearchQuery()}
                    onInput={(e) => setChatSearchQuery(e.currentTarget.value)}
                    class="w-full pl-8 pr-2 py-1.5 bg-canvas border border-primary-border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary-border focus:border-primary-border transition-colors"
                  />
                </div>

                <For each={filteredRecentChats()}>
                  {(item) => (
                    <StTooltip placement="right" content={<div>{item.title}</div>}>
                      <StTooltipTrigger as="div" class="w-full -ml-2">
                        <A
                          activeClass="bg-canvas-hover"
                          class="py-1 px-2 rounded-md hover:bg-panel-hover block w-full"
                          href={urls.chatPage(item.chatId)}
                        >
                          <span class="block truncate">{item.title}</span>
                        </A>
                      </StTooltipTrigger>
                    </StTooltip>
                  )}
                </For>
              </div>
            </Suspense>
          </div>

          {/* Changelog Button */}
          <div class="px-4 py-2">
            <button
              type="button"
              data-featurebase-changelog
              class="w-full py-2 px-3 text-sm font-medium text-primary-text bg-background hover:bg-panel-hover border border-brand rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
            >
              🎉
              <span class="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 text-transparent">
                Latest Changes
              </span>{" "}
              <span id="fb-update-badge" />
            </button>
          </div>

          {/* Fixed Footer Sections */}
          <div class="flex-shrink-0">
            {/* User Profile Footer */}
            <div class="px-4 py-3 border-t border-primary-border bg-background/60 dark:bg-[#1F2538]/60">
              <Switch>
                <Match when={!wire.auth?.isReady()}>
                  <TbLoader2 class="animate-spin size-6" />
                </Match>
                <Match when={!wire.auth?.isRegistered()}>
                  <div class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover dark:hover:bg-dark-surface-hover transition-colors cursor-pointer">
                    <div class="flex items-center justify-center gap-4 flex-auto">
                      <A href="/auth/login" class="font-medium px-2 py-1">
                        Log in
                      </A>
                      <A href="/auth/signup" class="font-medium px-4 py-1 rounded text-on-brand bg-brand shadow-xl">
                        Sign up
                      </A>
                    </div>
                  </div>
                </Match>
                <Match when={true}>
                  <StDropdown
                    modal={false}
                    placement="top-end"
                    gutter={5}
                    contentProps={{
                      class: "z-50",
                    }}
                    content={<UserAccountPopover />}
                  >
                    <StDropdownTrigger
                      as="div"
                      class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover dark:hover:bg-dark-surface-hover transition-colors cursor-pointer"
                    >
                      <div class="flex-shrink-0">
                        <Avatar src={wire.auth?.user()?.avatarUrl} name={wire.auth?.user()?.displayName} size="md" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="text-sm font-medium text-on-background dark:text-dark-on-background overflow-text-ellipsis overflow-hidden text-nowrap">
                          {wire.auth?.user()?.displayName || "You"}
                        </div>
                      </div>
                      <div class="flex-shrink-0 flex gap-1">
                        <button type="button" class="p-1 rounded hover:bg-panel-hover transition-colors">
                          <TbSettings class="size-5" />
                        </button>
                      </div>
                    </StDropdownTrigger>
                  </StDropdown>
                </Match>
              </Switch>
            </div>
          </div>

          {/* Resize handle */}
          <div
            ref={resizeHandleRef}
            class="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-primary/50 dark:hover:bg-dark-primary/50 transition-colors"
            onMouseDown={handleResizeStart}
            aria-label="Resize navigation pane"
          />

          {/* Extra pane */}
        </nav>
      </Show>

      {/* Project Switcher Modal */}
      <ProjectSwitcherModal open={isProjectModalOpen()} onOpenChange={setIsProjectModalOpen} />
    </>
  );
};
